import AsyncStorage from '@react-native-async-storage/async-storage';
import { sameCredential, type VibeClaimCredential } from './vibeClaimLinks';

// The one pending web-answers claim on this device. Persisted, so it survives
// cold starts, sign-in, registration + onboarding, email verification and
// navigation resets; cleared ONLY when the server gives a final answer for it
// (claimed / already played / round closed / invalid) or the user dismisses it.
// A newer link replaces an older one (the player's latest game wins).

export type PendingVibeClaimSource = 'link' | 'install_referrer' | 'code_entry';

export type PendingVibeClaim = {
  credential: VibeClaimCredential;
  source: PendingVibeClaimSource;
  savedAt: number;
};

const STORAGE_KEY = 'tonyte.vibeClaim.pending.v1';
type Listener = (pending: PendingVibeClaim | null) => void;

let cache: PendingVibeClaim | null | undefined;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach(listener => listener(cache ?? null));
}

function isPendingVibeClaim(value: unknown): value is PendingVibeClaim {
  const v = value as PendingVibeClaim | null;
  return Boolean(
    v && typeof v === 'object' && v.credential && typeof v.savedAt === 'number' &&
    (typeof (v.credential as { ref?: unknown }).ref === 'string' || typeof (v.credential as { code?: unknown }).code === 'string'),
  );
}

export async function loadPendingVibeClaim(): Promise<PendingVibeClaim | null> {
  if (cache !== undefined) return cache;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    cache = isPendingVibeClaim(parsed) ? parsed : null;
  } catch {
    cache = null;
  }
  return cache;
}

export async function savePendingVibeClaim(credential: VibeClaimCredential, source: PendingVibeClaimSource): Promise<void> {
  cache = { credential, source, savedAt: Date.now() };
  emit();
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // in-memory copy still drives this session
  }
}

/**
 * Clears the pending claim — but only if it is still the one that was just
 * resolved, so a newer link that arrived mid-request is never thrown away.
 */
export async function clearPendingVibeClaim(resolved?: VibeClaimCredential): Promise<void> {
  const current = await loadPendingVibeClaim();
  if (resolved && current && !sameCredential(current.credential, resolved)) return;
  cache = null;
  emit();
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function subscribePendingVibeClaim(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: forget the in-memory cache so the next load re-reads storage. */
export function __resetPendingVibeClaimCacheForTests() {
  cache = undefined;
  listeners.clear();
}
